import SettingsIcon from "@mui/icons-material/Settings";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import FormControlLabel from "@mui/material/FormControlLabel";
import IconButton from "@mui/material/IconButton";
import Popover from "@mui/material/Popover";
import Stack from "@mui/material/Stack";
import Switch from "@mui/material/Switch";
import TextField from "@mui/material/TextField";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { TachographDueSettings, TachographPolicy } from "../models/Tachograph";
import { DEFAULT_DUE_SETTINGS, MAX_INTERVAL_DAYS, MAX_WARNING_DAYS } from "../utilities/tachographDueSettings";

type Props = {
  settings: TachographDueSettings;
  onChange: (change: Partial<TachographDueSettings>) => void;
  onReset: () => void;
  /** The download period kept in Geotab, shown for comparison; undefined while it loads. */
  geotabPolicy: TachographPolicy | undefined;
};

type DayField = "deviceIntervalDays" | "driverIntervalDays" | "warningDays";

const DAY_FIELDS: Array<{ field: DayField; min: number; max: number }> = [
  { field: "deviceIntervalDays", min: 1, max: MAX_INTERVAL_DAYS },
  { field: "driverIntervalDays", min: 1, max: MAX_INTERVAL_DAYS },
  { field: "warningDays", min: 0, max: MAX_WARNING_DAYS },
];

/** Gear button with the readout deadlines, the warning window and the row colouring. */
export function TachographDueSettingsButton({ settings, onChange, onReset, geotabPolicy }: Props) {
  const { t } = useTranslation();
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);

  return (
    <>
      <Tooltip title={t("tachograph.dueSettings.title")}>
        <IconButton aria-label={t("tachograph.dueSettings.title")} onClick={event => setAnchor(event.currentTarget)}>
          <SettingsIcon />
        </IconButton>
      </Tooltip>
      <Popover
        open={!!anchor}
        anchorEl={anchor}
        onClose={() => setAnchor(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        slotProps={{ paper: { sx: { width: 340, p: 2 }, role: "dialog", "aria-label": t("tachograph.dueSettings.title") } }}
      >
        {/* Mounted per opening, so the fields start from the saved values. */}
        {anchor ? (
          <DueSettingsForm settings={settings} onChange={onChange} onReset={onReset} geotabPolicy={geotabPolicy} />
        ) : null}
      </Popover>
    </>
  );
}

function DueSettingsForm({ settings, onChange, onReset, geotabPolicy }: Props) {
  const { t } = useTranslation();
  // The typed text, so a field may be empty or half-typed; only a valid number is saved.
  const [drafts, setDrafts] = useState<Record<DayField, string>>(() => toDrafts(settings));

  const reset = () => {
    onReset();
    setDrafts(toDrafts(DEFAULT_DUE_SETTINGS));
  };

  return (
    <Stack spacing={2}>
      <Typography variant="subtitle2">{t("tachograph.dueSettings.title")}</Typography>
      {DAY_FIELDS.map(({ field, min, max }) => {
        const days = Number(drafts[field]);
        const valid = drafts[field] !== "" && Number.isInteger(days) && days >= min && days <= max;
        return (
          <TextField
            key={field}
            size="small"
            type="number"
            label={t(`tachograph.dueSettings.${field}`)}
            value={drafts[field]}
            error={!valid}
            helperText={valid ? t(`tachograph.dueSettings.${field}Hint`) : t("tachograph.dueSettings.invalid", { min, max })}
            slotProps={{ htmlInput: { min, max, step: 1 } }}
            onChange={event => {
              const text = event.target.value;
              setDrafts(current => ({ ...current, [field]: text }));
              const next = Number(text);
              if (text !== "" && Number.isInteger(next) && next >= min && next <= max) onChange({ [field]: next });
            }}
          />
        );
      })}
      <FormControlLabel
        control={
          <Switch checked={settings.highlightRows} onChange={event => onChange({ highlightRows: event.target.checked })} />
        }
        label={t("tachograph.dueSettings.highlightRows")}
      />
      <Typography variant="caption" color="text.secondary">
        {t("tachograph.dueSettings.legalNote")}
        {geotabPolicy?.source === "geotab"
          ? ` ${t("tachograph.dueSettings.geotabNote", {
              device: geotabPolicy.deviceIntervalDays,
              driver: geotabPolicy.driverIntervalDays,
            })}`
          : null}
      </Typography>
      <Box>
        <Button size="small" onClick={reset}>
          {t("tachograph.dueSettings.reset")}
        </Button>
      </Box>
    </Stack>
  );
}

const toDrafts = (settings: TachographDueSettings): Record<DayField, string> => ({
  deviceIntervalDays: String(settings.deviceIntervalDays),
  driverIntervalDays: String(settings.driverIntervalDays),
  warningDays: String(settings.warningDays),
});
